import { ChangeDetectionStrategy, Component, effect, ElementRef, OnDestroy, OnInit, computed, inject, signal, viewChild } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Subscription, defer, interval, retry, startWith, switchMap, takeUntil, tap, timer } from 'rxjs';
import { AiCreditsService } from 'src/app/services/AIReports/ai-credits.service';
import { AiCreditsTopupService, TopupStatus } from 'src/app/services/AIReports/ai-credits-topup.service';
import { TopupReturnService } from 'src/app/services/AIReports/topup-return.service';

const POLL_INTERVAL_MS = 2000;
const POLL_TIMEOUT_MS = 30000;
const BALANCE_STREAM_RETRY_DELAY_MS = 5000;
const TRUSTED_REDIRECT_HOST_SUFFIX = '.vipps.no';
const ALL_LEVEL_CLASSES = ['level-green', 'level-orange', 'level-red', 'level-empty', 'level-loading'] as const;

export type TopupScreen = 'amount' | 'processing' | 'success' | 'error';

@Component({
  selector: 'app-credits-bar',
  standalone: true,
  imports: [CommonModule, TranslateModule],
  templateUrl: './credits-bar.component.html',
  styleUrl: './credits-bar.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CreditsBarComponent implements OnInit, OnDestroy {
  protected readonly credits = inject(AiCreditsService);
  private readonly topupService = inject(AiCreditsTopupService);
  private readonly topupReturnService = inject(TopupReturnService);
  private readonly translate = inject(TranslateService);
  private readonly topupDialog = viewChild.required<ElementRef<HTMLDialogElement>>('topupDialog');
  private readonly creditsNumberText = viewChild<ElementRef<HTMLSpanElement>>('creditsNumberText');
  private readonly signalBarsEl = viewChild<ElementRef<HTMLElement>>('signalBarsEl');
  private readonly creditsNumberEl = viewChild<ElementRef<HTMLElement>>('creditsNumberEl');
  private readonly bar1El = viewChild<ElementRef<HTMLElement>>('bar1El');
  private readonly bar2El = viewChild<ElementRef<HTMLElement>>('bar2El');
  private readonly bar3El = viewChild<ElementRef<HTMLElement>>('bar3El');
  private readonly bar4El = viewChild<ElementRef<HTMLElement>>('bar4El');

  private balanceWatchSub?: Subscription;
  private pollingSub?: Subscription;
  /** Reference of a poll that hit POLL_TIMEOUT_MS without resolving — set so tryAgain() can
   * resume checking the same payment instead of starting a new one (avoids double-charging
   * a payment that Vipps ends up capturing after our timeout window). */
  private timedOutReference: string | null = null;

  readonly screen = signal<TopupScreen>('amount');
  readonly amountNok = signal<number | null>(null);
  readonly balanceNok = signal<number | null>(null);
  readonly errorMessage = signal<string | null>(null);

  protected readonly config = this.topupService.config;
  private readonly langChange = toSignal(this.translate.onLangChange, { initialValue: null });

  /** Gates the bar/badges on both balance AND config having loaded. Without this, the bar would
   * render `_balance`'s/`config`'s unloaded defaults (0, null) as a real "empty" state — level-
   * empty bars, the "No Credits" badge — for the window before either GET resolves, misleading a
   * user who may actually have a full balance. */
  protected readonly creditsReady = computed(() => this.credits.balanceLoaded() && this.topupService.configLoaded());

  /** Falls back to the credits bar's own (freshly re-fetched) balance if a captured status
   * response omits balanceNok — avoids showing a fabricated "kr 0.00" to someone who just paid. */
  readonly displayBalance = computed(() => this.balanceNok() ?? this.credits.balance());

  /** Angular's DecimalPipe formats against the app's LOCALE_ID, which is never wired to the
   * active ngx-translate language anywhere in this app — it would always render period-decimal
   * regardless of UI language. Format explicitly against the current language instead so
   * Norwegian users see comma-decimal ("kr 100,00") rather than "kr 100.00". Shared by both the
   * bar's running balance and the top-up success screen — the success screen previously kept
   * using Angular's `number` pipe (LOCALE_ID-bound) and so didn't get this fix. */
  private formatNok(value: number): string {
    const locale = this.translate.currentLang === 'en' ? 'en-US' : 'nb-NO';
    return new Intl.NumberFormat(locale, { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(value);
  }

  protected readonly formattedBalance = computed(() => {
    this.langChange();
    return this.formatNok(this.credits.balance());
  });

  protected readonly formattedDisplayBalance = computed(() => {
    this.langChange();
    return this.formatNok(this.displayBalance());
  });

  /** Writes the balance text directly to #creditsNumberText's DOM node, which is otherwise left
   * empty in the template (FT-887 credits-bar investigation): confirmed formattedBalance()
   * always recomputes correctly when the balance changes, but the text intermittently never got
   * painted through a normal interpolation binding there. A prior version kept the interpolation
   * alongside this effect as an intended "fallback" — that doesn't work: setting
   * `.textContent` on the parent replaces all child nodes, including Ivy's own Text node for the
   * interpolation, so the two can't coexist as independent writers. This effect is the sole
   * writer by design; its scheduling is tied to the signal graph itself, not to whatever CD-tree/
   * zone timing was intermittently dropping the update via the old binding-only approach. */
  private readonly _writeCreditsNumberText = effect(() => {
    const text = this.formattedBalance();
    const el = this.creditsNumberText();
    if (el) el.nativeElement.textContent = text;
  });

  /** Same direct-DOM-write rationale as _writeCreditsNumberText, extended to the rest of the
   * bar's visual state (level class on the signal-bars container and the number, each bar's
   * active state) — anything gated behind an @if (the low/empty badges) is left on the normal
   * template binding, since converting those to always-rendered+style-toggled elements would
   * change their DOM presence contract (existing specs assert querySelector(...) is null when
   * hidden) for a symptom that's only been confirmed on the balance number so far. */
  private readonly _writeVisualState = effect(() => {
    const isReady = this.creditsReady();
    const levelClass = isReady ? this.credits.levelClass() : 'level-loading';
    const bars = this.credits.activeBars();

    const barsEl = this.signalBarsEl()?.nativeElement;
    if (barsEl) this.setExclusiveClass(barsEl, levelClass);

    const numberEl = this.creditsNumberEl()?.nativeElement;
    if (numberEl) this.setExclusiveClass(numberEl, levelClass);

    this.setActiveClass(this.bar1El(), bars.bar1);
    this.setActiveClass(this.bar2El(), bars.bar2);
    this.setActiveClass(this.bar3El(), bars.bar3);
    this.setActiveClass(this.bar4El(), bars.bar4);
  });

  private setExclusiveClass(el: HTMLElement, activeClass: string): void {
    for (const c of ALL_LEVEL_CLASSES) {
      el.classList.toggle(c, c === activeClass);
    }
  }

  private setActiveClass(ref: ElementRef<HTMLElement> | undefined, active: boolean): void {
    ref?.nativeElement.classList.toggle('active', active);
  }

  readonly hint = computed(() => {
    const cfg = this.config();
    const amount = this.amountNok();
    if (!cfg || !amount || amount <= 0) return null;
    return Math.round(amount * cfg.estimatedInteractionsPerNok);
  });

  /** Worked example shown before the user has typed anything, so the interactions-per-kr
   * ratio is visible up front rather than only after entering an amount. Uses minTopupNok
   * as the example amount (server-config-driven, not a hardcoded number). */
  readonly exampleHint = computed(() => {
    const cfg = this.config();
    if (!cfg) return null;
    return { amount: cfg.minTopupNok, count: Math.round(cfg.minTopupNok * cfg.estimatedInteractionsPerNok) };
  });

  readonly isValidAmount = computed(() => {
    const cfg = this.config();
    const amount = this.amountNok();
    if (!cfg || amount == null) return false;
    return amount >= cfg.minTopupNok && amount <= cfg.maxTopupNok;
  });

  ngOnInit(): void {
    // watchBalance() errors (e.g. a dropped connection) terminate the Observable per Rx
    // semantics — without retry, the very first transient error would silently end balance
    // updates for the rest of the component's lifetime. defer() re-invokes watchBalance() on
    // every retry so each reconnect attempt opens a fresh SSE source rather than re-subscribing
    // to the one-and-only (now-closed) source from the initial call.
    this.balanceWatchSub = defer(() => this.topupService.watchBalance()).pipe(
      tap({ error: (err) => console.error('[CreditsBar] Balance SSE stream error, reconnecting:', err) }),
      retry({ delay: () => timer(BALANCE_STREAM_RETRY_DELAY_MS) }),
    ).subscribe({
      next: (event) => this.credits.applyBalanceUpdate(event.balanceNok),
      error: (err) => console.error('[CreditsBar] Balance SSE stream permanently failed:', err),
    });

    if (this.topupReturnService.hasPending()) {
      this.openModal();
    }
  }

  ngOnDestroy(): void {
    this.balanceWatchSub?.unsubscribe();
    this.pollingSub?.unsubscribe();
  }

  openModal(): void {
    const pendingReference = this.topupReturnService.consume();
    if (pendingReference) {
      this.beginProcessing(pendingReference);
    } else {
      this.timedOutReference = null;
      this.screen.set('amount');
      this.amountNok.set(null);
      this.errorMessage.set(null);
      this.topupService.fetchConfig();
    }
    this.topupDialog().nativeElement.showModal();
  }

  closeModal(): void {
    // Tear down any in-flight polling — otherwise it keeps running in the background after
    // the dialog closes and can later fire screen.set('success'/'error'), yanking the UI
    // away from a fresh amount-entry session the user started by reopening the modal.
    this.pollingSub?.unsubscribe();
    this.topupDialog().nativeElement.close();
  }

  onDialogClick(event: MouseEvent): void {
    const dialog = this.topupDialog().nativeElement;
    const rect = dialog.getBoundingClientRect();
    if (
      event.clientX < rect.left ||
      event.clientX > rect.right ||
      event.clientY < rect.top ||
      event.clientY > rect.bottom
    ) {
      this.closeModal();
    }
  }

  payWithVipps(): void {
    const amount = this.amountNok();
    if (!this.isValidAmount() || amount == null) return;

    this.topupService.initiate(amount).subscribe({
      next: (res) => { this.redirectTo(res.redirectUrl); },
      error: () => {
        this.errorMessage.set(this.translate.instant('aiReport.lbl_credits_topup_error_generic'));
        this.screen.set('error');
      },
    });
  }

  /** Extracted so tests can spy on it instead of triggering a real browser navigation. */
  protected redirectTo(url: string): void {
    if (!this.isTrustedRedirectUrl(url)) {
      console.error('[CreditsBar] Refusing to redirect to untrusted URL:', url);
      this.errorMessage.set(this.translate.instant('aiReport.lbl_credits_topup_error_generic'));
      this.screen.set('error');
      return;
    }
    window.location.href = url;
  }

  /** initiate() calls our own backend, but the redirectUrl it returns is only as trustworthy
   * as that backend response — validate scheme/host before handing it to window.location.href
   * so a compromised or malformed response can't be used as an open redirect. */
  private isTrustedRedirectUrl(url: string): boolean {
    try {
      const parsed = new URL(url);
      return parsed.protocol === 'https:' &&
        (parsed.hostname === 'vipps.no' || parsed.hostname.endsWith(TRUSTED_REDIRECT_HOST_SUFFIX));
    } catch {
      return false;
    }
  }

  onAmountInput(event: Event): void {
    const value = (event.target as HTMLInputElement).valueAsNumber;
    this.amountNok.set(Number.isFinite(value) ? value : null);
  }

  tryAgain(): void {
    const resumeReference = this.timedOutReference;
    this.timedOutReference = null;

    if (resumeReference) {
      // The previous poll only timed out — it didn't fail. Resume checking the same
      // reference rather than routing back to the amount screen, where paying again risks
      // double-charging a payment Vipps ends up capturing after our timeout window.
      this.beginProcessing(resumeReference);
      return;
    }

    this.screen.set('amount');
    this.amountNok.set(null);
    this.errorMessage.set(null);
  }

  private beginProcessing(reference: string): void {
    this.screen.set('processing');
    this.pollingSub?.unsubscribe();
    this.timedOutReference = null;

    let resolved = false;
    this.pollingSub = interval(POLL_INTERVAL_MS).pipe(
      startWith(0),
      switchMap(() => this.topupService.checkStatus(reference)),
      takeUntil(timer(POLL_TIMEOUT_MS)),
    ).subscribe({
      next: (res) => {
        if (res.status === ('captured' as TopupStatus)) {
          resolved = true;
          if (res.balanceNok != null) {
            this.balanceNok.set(res.balanceNok);
            this.credits.applyBalanceUpdate(res.balanceNok);
          } else {
            // Contract says a captured status always includes balanceNok — don't trust that
            // blindly. Leave balanceNok unset so displayBalance() falls back reactively to
            // the credits bar's own balance, and refresh it so that fallback is current.
            this.credits.fetchBalance();
          }
          this.screen.set('success');
          this.pollingSub?.unsubscribe();
        } else if (res.status === ('failed' as TopupStatus)) {
          resolved = true;
          this.errorMessage.set(this.translate.instant('aiReport.lbl_credits_topup_error_failed'));
          this.screen.set('error');
          this.pollingSub?.unsubscribe();
        }
      },
      error: () => {
        resolved = true;
        this.errorMessage.set(this.translate.instant('aiReport.lbl_credits_topup_error_generic'));
        this.screen.set('error');
      },
      complete: () => {
        if (!resolved) {
          this.timedOutReference = reference;
          this.errorMessage.set(this.translate.instant('aiReport.lbl_credits_topup_error_timeout'));
          this.screen.set('error');
        }
      },
    });
  }
}
